import classnames from 'classnames/bind';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  TimeSeriesScale,
  type ChartOptions,
} from 'chart.js';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import 'chartjs-adapter-luxon';

import { DataWidget } from 'types/dashboard_widget';

import { HEATMAP_CHART_GRANULARITIES } from 'dashboards/services/heatmap';
import { resolveGranularity } from 'dashboards/services/granularity';
import { createCsvFile } from 'services/csv';
import { downloadFile } from 'services/download_file';
import { getDimensionsFromMetricsResponse } from 'dashboards/services/metric';
import { sanitizeDataWidgetFilters } from 'dashboards/components/DataWidgetFiltersInput/sanitize';
import { useHeatmapChart } from 'dashboards/hooks/useHeatmapChart';
import { useUserMetrics } from 'dashboards/hooks/useUserMetrics';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useTopLevelDataWidgetFilters } from 'dashboards/hooks/useTopLevelDataWidgetFilters';

import { ChartError } from 'dashboards/components/ChartError/ChartError';
import { ChartLoader } from 'dashboards/components/ChartLoader/ChartLoader';
import { Icon } from 'components/Icon/IconDeprecated';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import styles from './Chart.module.scss';

const c = classnames.bind(styles);

ChartJS.register(
  MatrixController,
  MatrixElement,
  CategoryScale,
  TimeSeriesScale
);

type HeatmapChartProps = { widget: DataWidget<'timeseries-heatmap'> };

export function HeatmapChart({ widget }: HeatmapChartProps) {
  const { metric, granularity: rawGranularity, ...query } = widget.query;

  const { start, stop, timezone } = useTimeRangeParams();

  const granularity = resolveGranularity(
    rawGranularity,
    { start, stop },
    HEATMAP_CHART_GRANULARITIES
  );

  const { data: userMetrics } = useUserMetrics();

  const columns = getDimensionsFromMetricsResponse(metric, userMetrics);

  const filters = useTopLevelDataWidgetFilters({
    filters: sanitizeDataWidgetFilters(widget.query.filters),
    columns,
  });

  const { seriesLabels, data, isLoading, error } = useHeatmapChart({
    metricName: metric,
    timeRange: { start, stop },
    timeZone: timezone,
    granularity,
    ...query,
    filters,
  });

  if (!data && isLoading) {
    return (
      <Widget widget={widget}>
        <ChartLoader />
      </Widget>
    );
  }

  if (error) {
    return (
      <Widget widget={widget}>
        <ChartError error={error.message} />
      </Widget>
    );
  }

  if (!data) {
    return null;
  }

  const options: ChartOptions<'matrix'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'category' as const,
        labels: data?.labels,
        offset: true,
        ticks: {
          display: true,
        },
        grid: {
          display: false,
        },
      },
      y: {
        type: 'category' as const,
        labels: seriesLabels,
        offset: true,
        ticks: {
          display: true,
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        callbacks: {
          label(context) {
            const dataPoint = context.dataset.data[context.dataIndex];

            if ('v' in dataPoint) {
              return [`${dataPoint.y}: ${dataPoint.v}`];
            }
          },
        },
      },
    },
  };

  const csv = {
    headers: ['label', ...data.labels],
    rows: seriesLabels.map((label) => {
      const rowValues = data.datasets[0].data
        .filter((point) => point.y === label)
        .map((point) => String(point.v));
      return [label, ...rowValues];
    }),
  };

  const menuItems = {
    csv: {
      children: 'Export as CSV',
      leftSection: <Icon name="export" size="small" />,
      onClick: () => downloadFile(widget.name, createCsvFile(csv)),
      disabled: csv.rows.length === 0,
    },
  };

  return (
    <Widget widget={widget} menuItems={menuItems}>
      <div className={c('chart')}>
        <Chart type="matrix" data={data} options={options} />
      </div>
    </Widget>
  );
}
